import jsPDF from "jspdf";
import { logoBase64 } from "../../../public/assets/logoBase64";
import { AgingProducerResponse } from "../interfaces/gestioncobros.interfaces";
import { TableConfig } from "../interfaces/table-config.interface";
import * as XLSX from 'xlsx';

interface params { data: AgingProducerResponse[], title: string, user?: string, tableConfig: TableConfig }

/**
 * Exporta en formato PDF un reporte de antigüedad de saldo por deudor.
 * Cada deudor se muestra con su nombre, teléfono y una tabla de pólizas con totales.
 *
 * @param params.data - Lista de deudores con sus datos de antigüedad.
 * @param params.title - Título principal del documento.
 * @param params.user - Nombre del usuario que genera el reporte.
 * @param params.tableConfig - Configuración de columnas para la tabla.
 */
export const exportarPDF = ({ data, title, user, tableConfig }: params) => {
  // Crear documento en formato horizontal
  const doc = new jsPDF({ orientation: "landscape" }) as any;
  const config = {
    headerFontSize: 10,
    debtorFontSize: 11,
    phoneFontSize: 9,
    sectionStartY: 30,
    sectionGap: 5,
    tableStartOffset: 5,
    margins: { left: 6, right: 6 },
    cellPadding: 1,
    bodyFontSize: 8,
    headFontSize: 8,
  };

  // Calcular la fecha del día anterior para mostrarla como fecha del reporte
  const yesterday = new Date(new Date().setDate(new Date().getDate() - 1));
  const date = yesterday.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });

  // Extraer etiquetas de columnas desde la configuración
  const columns = tableConfig.columns.map(c => c.label);

  // Posición vertical inicial para contenido
  let currentY = config.sectionStartY;
  const pageHeight = doc.internal.pageSize.height;

  /**
   * Renderiza el encabezado en cada página del PDF.
   */
  const pageHeader = (doc: any) => {
    doc.addImage(logoBase64, "PNG", 10, 10, 40, 13); // Logo de la empresa
    doc.setFontSize(config.headerFontSize);
    doc.text(`${title} \n${user} \nAl día ${date}`, 55, 15); // Título + usuario + fecha
  };

  // Dibujar encabezado inicial
  doc.autoTable({
    didDrawPage: () => pageHeader(doc),
  });

  // Iterar sobre cada deudor
  data.forEach(deudor => {
    // Verificar si hay suficiente espacio, si no se agrega nueva página
    if (currentY + 42 > pageHeight) {
      doc.addPage();
      doc.autoTable({
        didDrawPage: () => pageHeader(doc),
      });
      currentY = config.sectionStartY;
    }

    // Mostrar nombre a la izquierda y teléfono a la derecha en la misma línea
    const pageWidth = doc.internal.pageSize.width;
    doc.setFontSize(config.debtorFontSize);
    doc.text(`${deudor.debtorId}    ${deudor.debtorName}`, 10, currentY);
    doc.setFontSize(config.phoneFontSize);
    doc.text(`Teléfono: ${deudor.phoneNumber}`, pageWidth - config.margins.right, currentY, { align: 'right' });

    // Crear el cuerpo de la tabla de pólizas del deudor
    let body = deudor.agingProducers.map((policy) => [
      policy.policyNumber,
      formatAmount(policy.balance),
      formatAmount(policy.noVencido),
      formatAmount(policy.timeframe1),
      formatAmount(policy.timeframe2),
      formatAmount(policy.timeframe3),
      formatAmount(policy.timeframe4),
      formatAmount(policy.timeframe5),
      formatAmount(policy.timeframe6),
      formatAmount(policy.timeframe7),
      formatAmount(policy.timeframe8),
    ]);

    // Insertar encabezado de la tabla
    body.unshift(columns);

    // Calcular totales por deudor
    const totalBalance = sumBy(deudor.agingProducers, 'balance');
    const totalNoVencido = sumBy(deudor.agingProducers, 'noVencido');
    const totalTimeFrame1 = sumBy(deudor.agingProducers, 'timeframe1');
    const totalTimeFrame2 = sumBy(deudor.agingProducers, 'timeframe2');
    const totalTimeFrame3 = sumBy(deudor.agingProducers, 'timeframe3');
    const totalTimeFrame4 = sumBy(deudor.agingProducers, 'timeframe4');
    const totalTimeFrame5 = sumBy(deudor.agingProducers, 'timeframe5');
    const totalTimeFrame6 = sumBy(deudor.agingProducers, 'timeframe6');
    const totalTimeFrame7 = sumBy(deudor.agingProducers, 'timeframe7');
    const totalTimeFrame8 = sumBy(deudor.agingProducers, 'timeframe8');

    // Agregar fila de totales al cuerpo de la tabla
    body.push([
      'Totales Deudor',
      formatAmount(totalBalance),
      formatAmount(totalNoVencido),
      formatAmount(totalTimeFrame1),
      formatAmount(totalTimeFrame2),
      formatAmount(totalTimeFrame3),
      formatAmount(totalTimeFrame4),
      formatAmount(totalTimeFrame5),
      formatAmount(totalTimeFrame6),
      formatAmount(totalTimeFrame7),
      formatAmount(totalTimeFrame8),
    ]);

    // Dibujar tabla en PDF
    doc.autoTable({
      startY: currentY + config.tableStartOffset,
      body: body,
      margin: config.margins,
      styles: {
        fontSize: config.bodyFontSize,
        cellPadding: config.cellPadding,
      },
      headStyles: {
        fontSize: config.headFontSize,
        cellPadding: config.cellPadding,
      },
      columnStyles: {
        0: { halign: 'left' },
        1: { halign: 'right' },
        2: { halign: 'right' },
        3: { halign: 'right' },
        4: { halign: 'right' },
        5: { halign: 'right' },
        6: { halign: 'right' },
        7: { halign: 'right' },
        8: { halign: 'right' },
        9: { halign: 'right' },
        10: { halign: 'right' },
      },
      /**
       * Personaliza el estilo de las celdas.
       */
      didParseCell: function (row: any) {
        if (row.row.index === 0 && row.section === 'body') {
          // Estilo para encabezado de columnas
          Object.assign(row.cell.styles, {
            valign: 'bottom',
            lineWidth: { bottom: 1 },
            lineColor: [255, 165, 0],
            fillColor: [255, 255, 255],
            textColor: [0, 0, 0],
            fontStyle: 'bold',
            fontSize: config.headFontSize
          });
        }

        if (row.row.index === row.table.body.length - 1 && row.section === 'body') {
          // Estilo para fila de totales
          Object.assign(row.cell.styles, {
            fillColor: [255, 255, 255],
            textColor: [0, 0, 0],
            fontStyle: 'bold',
            fontSize: config.bodyFontSize,
            lineWidth: { top: 0.5 },
            lineColor: [0, 0, 0],
          });
        }
      }
    });

    // Actualizar posición vertical para el siguiente deudor
    currentY = doc.lastAutoTable.finalY + config.sectionGap;
  });

  // Guardar el archivo PDF
  doc.save("Antigüedad.pdf");
};


/**
 * Exporta un reporte de antigüedad en formato Word usando HTML y estilos embebidos.
 * Cada deudor se muestra con sus pólizas en una tabla independiente.
 *
 * @param params.data - Lista de deudores con sus datos de antigüedad.
 * @param params.title - Título principal del documento.
 * @param params.user - Nombre del usuario que genera el reporte.
 * @param params.tableConfig - Configuración de columnas para la tabla.
 */
export const exportarWord = ({ data, title, user, tableConfig }: params) => {
  // Obtener fecha actual en formato legible
  const today = new Date().toLocaleDateString('es-ES', {
    weekday: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  // Extraer etiquetas de columnas desde la configuración
  const columns = tableConfig.columns.map(c => c.label);

  // Estructura HTML inicial del documento con estilos
  let content = `
     <html>
        <head>
          <style>
            @page { size: A4 landscape; margin: 10mm; }
            body { font-family: Arial, sans-serif; margin: 8px; }
            table { width: 100%; border-collapse: collapse; margin-top: 8px; font-size: 9px; table-layout: fixed; }
            th { background-color: #2980B9; color: white; padding: 4px; text-align: left; border: 1px solid #ddd; }
            td { padding: 3px 4px; border: 1px solid #ddd; }
            .header { display: flex; margin-bottom: 8px; }
            .title { font-size: 16px; font-weight: bold; }
            .subtitle { font-size: 12px; }
            .date { font-size: 11px; margin-top: 3px; }
            .spacer { margin-top: 6px; }
            .debtor-section { margin-top: 10px; page-break-inside: avoid; }
            .debtor-section:last-child { page-break-after: auto; }
            .debtor-header { display: flex; justify-content: space-between; align-items: baseline; }
            .image { width: 40px }
          </style>
        </head>
        <body>
    `;

  // Encabezado con título, usuario, fecha y logo
  content += `
        <div class="header">
            <div><img class='image' src="${logoBase64}" /></div>
            <div>
                <div class="title">${title}</div>
                <div class="user">${user}</div>
                <div class="date">Al día: ${today}</div>
            </div>
            <div class="spacer"></div>
        </div>
  `;

  // Iterar por cada deudor para mostrar sus pólizas
  data.forEach(deudor => {
    content += `
      <div class="debtor-section">
        <div class="debtor-header">
          <div class="subtitle"><strong>Deudor:</strong> ${deudor.debtorName}</div>
          <div class="subtitle"><strong>Teléfono:</strong> ${deudor.phoneNumber}</div>
        </div>
        <table>
          <thead><tr>`;

    // Insertar encabezados de columna
    columns.forEach(col => {
      content += `<th>${col}</th>`;
    });

    content += `</tr></thead><tbody>`;

    // Insertar filas con pólizas del deudor
    deudor.agingProducers.forEach(policy => {
      content += `
        <tr>
          <td>${policy.policyNumber}</td>
          <td>${formatAmount(policy.balance)}</td>
          <td>${formatAmount(policy.noVencido)}</td>
          <td>${formatAmount(policy.timeframe1)}</td>
          <td>${formatAmount(policy.timeframe2)}</td>
          <td>${formatAmount(policy.timeframe3)}</td>
          <td>${formatAmount(policy.timeframe4)}</td>
          <td>${formatAmount(policy.timeframe5)}</td>
          <td>${formatAmount(policy.timeframe6)}</td>
          <td>${formatAmount(policy.timeframe7)}</td>
          <td>${formatAmount(policy.timeframe8)}</td>
        </tr>`;
    });

    content += `</tbody></table></div>`;
  });

  content += `</body></html>`;

  // Crear archivo .doc a partir del HTML generado
  const blob = new Blob([content], { type: 'application/msword' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'Antigüedad.doc';
  a.click();
  window.URL.revokeObjectURL(url);
};


/**
 * Exporta un reporte de antigüedad en formato texto plano (.txt).
 * Muestra deudor, productor y pólizas con montos en formato separado por "|".
 *
 * @param params.data - Datos de antigüedad a exportar.
 * @param params.title - Título del archivo de texto.
 * @param params.tableConfig - Configuración de columnas (no usada en esta función).
 */
export const exportarTexto = ({ data, title, tableConfig }: params) => {
  // Generar fecha del día anterior
  const yesterday = new Date(new Date().setDate(new Date().getDate() - 1));
  const date = yesterday.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });

  let content = "";

  // Agregar título si está presente
  if (title) content += `${title}\n\n`;

  // Iterar sobre cada deudor
  data.forEach((deudor) => {
    // Encabezado del deudor
    content += `Deudor: ${deudor.debtorName}\nTeléfono: ${deudor.phoneNumber}\nProductor: ${deudor.producerName}\nAl día: ${date}\n\n`;

    // Agregar filas de pólizas
    deudor.agingProducers.forEach((policy) => {
      const row = [
        policy.policyNumber,
        formatAmount(policy.balance),
        formatAmount(policy.noVencido),
        formatAmount(policy.timeframe1),
        formatAmount(policy.timeframe2),
        formatAmount(policy.timeframe3),
        formatAmount(policy.timeframe4),
        formatAmount(policy.timeframe5),
        formatAmount(policy.timeframe6),
        formatAmount(policy.timeframe7),
        formatAmount(policy.timeframe8),
      ].join("|");
      content += row + "\n";
    });

    content += "\n\n\n"; // Espacio entre deudores
  });

  // Crear y descargar el archivo de texto
  const blob = new Blob([content], { type: "text/plain" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "Antigüedad.txt";
  a.click();
  window.URL.revokeObjectURL(url);
};


/**
 * Exporta el reporte de antigüedad por deudor en formato Excel.
 * Cada deudor tendrá su propia hoja con encabezados, información y pólizas.
 *
 * @param params.data - Datos agrupados por deudor a exportar.
 * @param params.title - Título del reporte.
 * @param params.user - Usuario que genera el archivo.
 * @param params.tableConfig - Configuración de columnas para Excel.
 */
export const exportarExcel = ({ data, title, user, tableConfig }: params) => {
  const wb = XLSX.utils.book_new(); // Crear libro nuevo

  // Fecha del día anterior como referencia
  const yesterday = new Date(new Date().setDate(new Date().getDate() - 1));
  const date = yesterday.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });

  const columns = tableConfig.columns.map(c => c.label); // Encabezados

  // Iterar por cada deudor
  data.forEach((deudor) => {
    const ws = XLSX.utils.aoa_to_sheet([]); // Nueva hoja
    let rowIndex = 0;

    // Agregar título, usuario y fecha
    XLSX.utils.sheet_add_aoa(ws, [[title]], { origin: `A${++rowIndex}` });
    XLSX.utils.sheet_add_aoa(ws, [[user]], { origin: `A${++rowIndex}` });
    XLSX.utils.sheet_add_aoa(ws, [[`Al día: ${date}`]], { origin: `A${++rowIndex}` });

    // Datos del deudor
    XLSX.utils.sheet_add_aoa(ws, [[`Deudor: ${deudor.debtorName}`]], { origin: `A${++rowIndex}` });
    XLSX.utils.sheet_add_aoa(ws, [[`Teléfono: ${deudor.phoneNumber}`]], { origin: `A${++rowIndex}` });

    rowIndex++; // Espacio en blanco

    // Encabezados de tabla
    XLSX.utils.sheet_add_aoa(ws, [columns], { origin: `A${++rowIndex}` });

    // Filas de pólizas
    const datos = deudor.agingProducers.map((item) => [
      item.policyNumber,
      item.balance,
      item.noVencido,
      item.timeframe1,
      item.timeframe2,
      item.timeframe3,
      item.timeframe4,
      item.timeframe5,
      item.timeframe6,
      item.timeframe7,
      item.timeframe8,
    ]);

    XLSX.utils.sheet_add_aoa(ws, datos, { origin: `A${++rowIndex}` });

    // Ajustar ancho de columnas
    const wscols = columns.map(() => ({ wch: 20 }));
    ws['!cols'] = wscols;

    // Agregar hoja al libro
    XLSX.utils.book_append_sheet(wb, ws, deudor.debtorName.slice(0, 31)); // Excel permite máx. 31 caracteres
  });

  // Guardar el archivo
  XLSX.writeFile(wb, 'Antigüedad.xlsx');
};



/**
 * Formatea un número como monto en pesos dominicanos.
 *
 * @param value - Número a formatear.
 * @returns Monto con separador de miles y dos decimales.
 */
const formatAmount = (value: number): string =>
  new Intl.NumberFormat('es-DO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);


/**
 * Suma todos los valores de una propiedad numérica dentro de un arreglo de objetos.
 *
 * @param arr - Arreglo de objetos.
 * @param key - Clave del valor numérico a sumar.
 * @returns Suma total.
 */
const sumBy = (arr: any[], key: string): number =>
  arr.reduce((acc, cur) => acc + cur[key], 0);
